import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, FolderOpen, Download, Upload } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/api";
import { MEETING_TYPES, formatDate } from "@/constants";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFiles() {
  const [minutes, setMinutes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [minuteDialog, setMinuteDialog] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [minuteForm, setMinuteForm] = useState({ title: "", meeting_type: "Conseil d'école", meeting_date: "", summary: "", published: true });
  const [docForm, setDocForm] = useState({ title: "", description: "", category: "Général", published: true });
  const [minuteFile, setMinuteFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const minuteFileRef = useRef(null);
  const docFileRef = useRef(null);

  const loadMinutes = () => api.get("/admin/minutes").then((r) => setMinutes(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadDocs = () => api.get("/admin/documents").then((r) => setDocuments(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { loadMinutes(); loadDocs(); }, []);

  const submitMinute = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", minuteForm.title);
      fd.append("meeting_type", minuteForm.meeting_type);
      fd.append("meeting_date", minuteForm.meeting_date);
      fd.append("summary", minuteForm.summary);
      fd.append("published", minuteForm.published);
      if (minuteFile) fd.append("file", minuteFile);
      await api.post("/admin/minutes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Compte rendu publié");
      setMinuteDialog(false);
      setMinuteForm({ title: "", meeting_type: "Conseil d'école", meeting_date: "", summary: "", published: true });
      setMinuteFile(null);
      if (minuteFileRef.current) minuteFileRef.current.value = "";
      loadMinutes();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitDoc = async (e) => {
    e.preventDefault();
    if (!docFile) {
      toast.error("Sélectionnez un fichier à publier");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", docForm.title);
      fd.append("description", docForm.description);
      fd.append("category", docForm.category);
      fd.append("published", docForm.published);
      fd.append("file", docFile);
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document publié");
      setDocDialog(false);
      setDocForm({ title: "", description: "", category: "Général", published: true });
      setDocFile(null);
      if (docFileRef.current) docFileRef.current.value = "";
      loadDocs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (kind, item) => {
    try {
      await api.patch(`/admin/${kind}/${item.id}`, { published: !item.published });
      toast.success(item.published ? "Passé en brouillon" : "Publié");
      kind === "minutes" ? loadMinutes() : loadDocs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (kind, item, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;
    try {
      await api.delete(`/admin/${kind}/${item.id}`);
      toast.success("Supprimé");
      kind === "minutes" ? loadMinutes() : loadDocs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pubBadge = (published) => (
    <Badge className={published
      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
      : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}>
      {published ? "Publié" : "Brouillon"}
    </Badge>
  );

  return (
    <div className="space-y-6" data-testid="admin-files-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-files-title">Documents & comptes rendus</h1>
        <p className="text-sm text-slate-500 mt-1">Fichiers internes réservés aux adhérents connectés (PDF, 10 Mo max).</p>
      </div>

      <Tabs defaultValue="minutes">
        <TabsList className="mb-6">
          <TabsTrigger value="minutes" data-testid="admin-tab-minutes">Comptes rendus ({minutes.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="admin-tab-documents">Documents ({documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="minutes" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => setMinuteDialog(true)} data-testid="minute-create-btn">
              <Plus className="h-4 w-4 mr-2" />Nouveau compte rendu
            </Button>
          </div>
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="minutes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Fichier</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {minutes.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8" data-testid="minutes-admin-empty">
                      Aucun compte rendu publié.
                    </TableCell></TableRow>
                  )}
                  {minutes.map((m) => (
                    <TableRow key={m.id} data-testid={`minute-row-${m.id}`}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-sky-700 shrink-0" />{m.title}</span>
                      </TableCell>
                      <TableCell className="text-sm">{m.meeting_type}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatDate(m.meeting_date)}</TableCell>
                      <TableCell>
                        {m.storage_path ? (
                          <Button variant="ghost" size="sm" asChild data-testid={`minute-admin-download-${m.id}`}>
                            <a href={`${API_BASE}/files/${m.storage_path}`} target="_blank" rel="noopener noreferrer" className="text-sky-700">
                              <Download className="h-3.5 w-3.5 mr-1" />{m.original_filename}
                            </a>
                          </Button>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      <TableCell>
                        <button onClick={() => togglePublish("minutes", m)} data-testid={`minute-toggle-${m.id}`}>{pubBadge(m.published)}</button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => remove("minutes", m, m.title)} data-testid={`minute-delete-${m.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => setDocDialog(true)} data-testid="document-create-btn">
              <Plus className="h-4 w-4 mr-2" />Nouveau document
            </Button>
          </div>
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="documents-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Fichier</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8" data-testid="documents-admin-empty">
                      Aucun document publié.
                    </TableCell></TableRow>
                  )}
                  {documents.map((d) => (
                    <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2"><FolderOpen className="h-4 w-4 text-emerald-700 shrink-0" />{d.title}</span>
                      </TableCell>
                      <TableCell className="text-sm">{d.category}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild data-testid={`document-admin-download-${d.id}`}>
                          <a href={`${API_BASE}/files/${d.storage_path}`} target="_blank" rel="noopener noreferrer" className="text-sky-700">
                            <Download className="h-3.5 w-3.5 mr-1" />{d.original_filename}
                          </a>
                        </Button>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => togglePublish("documents", d)} data-testid={`document-toggle-${d.id}`}>{pubBadge(d.published)}</button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => remove("documents", d, d.title)} data-testid={`document-delete-${d.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={minuteDialog} onOpenChange={setMinuteDialog}>
        <DialogContent className="max-w-lg" data-testid="minute-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouveau compte rendu</DialogTitle>
            <DialogDescription>Compte rendu de réunion, téléchargeable par les adhérents une fois publié.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMinute} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input required value={minuteForm.title} onChange={(e) => setMinuteForm({ ...minuteForm, title: e.target.value })} data-testid="minute-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type de réunion</Label>
                <Select value={minuteForm.meeting_type} onValueChange={(v) => setMinuteForm({ ...minuteForm, meeting_type: v })}>
                  <SelectTrigger data-testid="minute-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date de la réunion</Label>
                <Input type="date" required value={minuteForm.meeting_date} onChange={(e) => setMinuteForm({ ...minuteForm, meeting_date: e.target.value })} data-testid="minute-form-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Résumé (optionnel)</Label>
              <Textarea rows={3} value={minuteForm.summary} onChange={(e) => setMinuteForm({ ...minuteForm, summary: e.target.value })} data-testid="minute-form-summary" />
            </div>
            <div className="space-y-2">
              <Label>Fichier PDF (optionnel)</Label>
              <Input ref={minuteFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.txt,.docx,.xlsx"
                onChange={(e) => setMinuteFile(e.target.files[0] || null)} data-testid="minute-form-file" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <Label htmlFor="minute-published" className="font-normal text-sm">Visible par les adhérents</Label>
              <Switch id="minute-published" checked={minuteForm.published}
                onCheckedChange={(v) => setMinuteForm({ ...minuteForm, published: v })} data-testid="minute-form-published" />
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="minute-form-submit">
              <Upload className="h-4 w-4 mr-2" />{saving ? "Publication…" : "Publier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="max-w-lg" data-testid="document-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouveau document interne</DialogTitle>
            <DialogDescription>Document interne téléchargeable par les adhérents une fois publié.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDoc} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input required value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} data-testid="document-form-title" />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={docForm.category} onValueChange={(v) => setDocForm({ ...docForm, category: v })}>
                <SelectTrigger data-testid="document-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Général", "Statuts & chartes", "Comptes", "Formulaires", "Autre"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optionnelle)</Label>
              <Textarea rows={3} value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} data-testid="document-form-description" />
            </div>
            <div className="space-y-2">
              <Label>Fichier (PDF, image, texte — 10 Mo max)</Label>
              <Input ref={docFileRef} type="file" required accept=".pdf,.png,.jpg,.jpeg,.txt,.docx,.xlsx"
                onChange={(e) => setDocFile(e.target.files[0] || null)} data-testid="document-form-file" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <Label htmlFor="document-published" className="font-normal text-sm">Visible par les adhérents</Label>
              <Switch id="document-published" checked={docForm.published}
                onCheckedChange={(v) => setDocForm({ ...docForm, published: v })} data-testid="document-form-published" />
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="document-form-submit">
              <Upload className="h-4 w-4 mr-2" />{saving ? "Publication…" : "Publier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
